import { useState } from "react";
import Select, { SelectOption } from "@amzn/meridian/select";
import Plot from "react-plotly.js";

interface ChartViewerProps {
  csvPreview: string;
}

const DEFAULT_PLOTLY_CONFIG = {
  displayModeBar: true,
  displaylogo: false,
  responsive: true,
};

/**
 * ChartViewer component renders interactive charts from CSV data
 * Supports bar, line, and pie chart types with user selection
 */
const ChartViewer = ({ csvPreview }: ChartViewerProps) => {
  // Track selected chart type, defaults to bar chart
  const [chartType, setChartType] = useState("bar");

  /**
   * Parses CSV preview data and generates Plotly chart configuration
   * - Line charts: Support multiple data series from all numeric columns
   * - Bar/Pie charts: Use only first two columns for clarity
   * - First column: x-axis labels/categories
   */
  const generateChartData = () => {
    if (!csvPreview) return { data: [], layout: {} };

    // Parse CSV lines and validate data
    const lines = csvPreview.trim().split("\n");
    if (lines.length < 2) return { data: [], layout: {} };

    // Extract headers and data rows
    const headers = lines[0].split(",");
    const rows = lines.slice(1).map((line) => line.split(","));

    // Map x-axis data (first column)
    const xData = rows.map((row) => row[0]);

    // Generate chart data based on selected type
    let data;
    switch (chartType) {
      case "pie":
        // Pie charts use only first two columns
        const yData = rows.map((row) => parseFloat(row[1]) || 0);
        data = [
          {
            type: "pie",
            labels: xData,
            values: yData,
          },
        ];
        break;
      case "line":
        // Line charts support multiple data series for better trend visualization
        data = headers.slice(1).map((header, index) => {
          const columnIndex = index + 1;
          const yValues = rows.map((row) => parseFloat(row[columnIndex]) || 0);
          return {
            type: "scatter",
            mode: "lines+markers",
            x: xData,
            y: yValues,
            name: header,
          };
        });
        break;
      default: // bar
        // Bar charts use only first two columns to avoid clutter
        const barYData = rows.map((row) => parseFloat(row[1]) || 0);
        data = [
          {
            type: "bar",
            x: xData,
            y: barYData,
            name: headers[1] || "Value",
          },
        ];
    }

    // Configure chart layout with titles and margins
    const layout = {
      title: `Data by ${headers[0] || "Category"}`,
      xaxis: {
        title: headers[0] || "Category",
        type: "category", // Force categorical axis to prevent date auto-conversion
      },
      yaxis: { title: "Value" },
      margin: { t: 80, r: 50, b: 50, l: 50 }, // Increased top margin for mode bar spacing
    };

    return { data, layout };
  };

  const chartJson = generateChartData();

  return (
    <div>
      {/* Chart type selector */}
      <div style={{ marginBottom: "16px" }}>
        <Select value={chartType} onChange={(value) => setChartType(value)}>
          <SelectOption value="bar" label="Bar Chart" />
          <SelectOption value="line" label="Line Chart" />
          <SelectOption value="pie" label="Pie Chart" />
        </Select>
      </div>
      {/* Plotly chart with download and interaction tools */}
      <Plot
        style={{ width: "700px", height: "450px" }}
        data={chartJson.data}
        layout={chartJson.layout}
        config={DEFAULT_PLOTLY_CONFIG}
      />
    </div>
  );
};

export default ChartViewer;
